import React from 'react';
import { connect } from 'react-redux';
import { noop, omitBy } from 'lodash';
import { toWei, checkAddressChecksum } from 'web3-utils';
import safeToBn from '@blockcatio/tabby-library/lib/utilities/safeToBn';
import AddressImage from '@blockcatio/tabby-library/lib/components/AddressImage';
import { ZERO_ADDRESS } from '@blockcatio/tabby-library/lib/constants';
import { convertTokenToLowestUnit } from '@blockcatio/tabby-library/lib/utilities/tokenUtilities';
import FormattedWei from '@blockcatio/tabby-library/lib/components/FormattedWei';
import isAddress from '@blockcatio/tabby-library/lib/utilities/isAddress';

import CostCalculatorContainer from './CostCalculatorContainer';
import PaymentForm from './PaymentForm';
import actions from '../actions';
import { receiverBalanceFetch } from '../actions/account';
import { walletCanSend } from '../selectors/account';
import { getTokenListByName } from '../selectors/token';
import { getGasLimits } from '../selectors/app';
import getCreatePaymentGasLimit from '../utilities/getCreatePaymentGasLimit';
import { weiToFiat } from '../utilities/helpers';
import formatPaymentAmount from '../utilities/formatPaymentAmount';
import isValidFloat from '../utilities/isValidFloat';
import getPaymentDecimals from '../utilities/getPaymentDecimals';
import { BACKUP_GAS_PRICE, ADDRESS_CHECKSUM_WARNING } from '../constants';

const BigNumber = require('bignumber.js');

const mapStateToProps = (state) => {
  const tokenList = getTokenListByName(state);
  const { currencyCode } = state.currencyExchangeRates;
  return {
    currentAddress: state.account.address,
    balance: state.account.balance,
    catBalance: state.account.catBalance,
    catAllowance: state.account.catAllowance,
    payWithEthFee: state.app.payWithEthFee,
    payWithCatFee: state.app.payWithCatFee,
    canSendPayments: walletCanSend(state),
    recommendedGasPrice: state.app.recommendedGasPrices.standard || 0,
    tokenList,
    disabled: !walletCanSend(state),
    formatPaymentAmount: (amount, tokenAddress, decimals) =>
      formatPaymentAmount(amount, tokenAddress, tokenList, decimals),
    getCreatePaymentGasLimit: (tokenAddress, payWithCat) =>
      getCreatePaymentGasLimit(tokenAddress, payWithCat, getGasLimits(state)),
    catEthExchangeRate: state.currencyExchangeRates.catEthExchangeRate || 0,
    ethFiatExchangeRate: state.currencyExchangeRates.ethExchangeRates[currencyCode] || 0,
    currencyCode,
    acceptPaymentGasLimit: state.app.gasLimits.acceptPaymentGasLimit,
  };
};

const mapDispatchToProps = dispatch => ({
  sendPayment: (...args) => dispatch(actions.paymentSend(...args)),
  onWalletReset: () => dispatch(actions.accountWalletSoftReset()),
  authorizeToken: (...args) => dispatch(actions.authorizeToken(...args)),
  onTokenAddressChange: (...args) => dispatch(actions.tokenAddressChange(...args)),
  addCustomToken: (...args) => dispatch(actions.addCustomToken(...args)),
  authorizeCat: (...args) => dispatch(actions.authorizeCat(...args)),
  onReceiverBalanceFetch: address => dispatch(receiverBalanceFetch(address)),
  validateTokenAddress: address => dispatch(actions.validateTokenAddress(address)),
  fetchTokenBalance: tokenAddress => dispatch(actions.fetchTokenBalance(tokenAddress)),
  fetchTokenAllowance: tokenAddress => dispatch(actions.fetchTokenAllowance(tokenAddress)),
});

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...ownProps,
  ...stateProps,
  ...dispatchProps,
  onSubmit: (...args) => {
    if (stateProps.canSendPayments) {
      return dispatchProps.sendPayment(...args);
    }
    return noop;
  },
});

export class PaymentFormContainer extends React.Component {
  static defaultProps = {
    disabled: false,
    catEthExchangeRate: 0,
    ethFiatExchangeRate: 0,
    currencyCode: 'usd',
  };

  constructor(props) {
    super(props);

    this.getDefaultState = this.getDefaultState.bind(this);
    this.getAmountToSend = this.getAmountToSend.bind(this);
    this.calculateGasForward = this.calculateGasForward.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.authorizeToken = this.authorizeToken.bind(this);
    this.canPayFee = this.canPayFee.bind(this);
    this.authorizeCatPayment = this.authorizeCatPayment.bind(this);
    this.handleCustomTokenFormSubmit = this.handleCustomTokenFormSubmit.bind(this);
    this.generateTokenOptions = this.generateTokenOptions.bind(this);

    this.state = {
      ...this.getDefaultState(),
      canPayFee: false,
    };
  }

  componentDidMount() {
    this.mounted = true;
  }

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.balance &&
      nextProps.catBalance &&
      (!this.props.balance || !this.props.catBalance)
    ) {
      this.canPayFee(
        {
          amount: this.state.amount,
          tokenAddress: this.state.tokenAddress,
          paymentType: this.state.paymentType,
        },
        nextProps.balance,
        nextProps.catBalance,
      );
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  getDefaultState() {
    return {
      amount: '',
      tokenAddress: ZERO_ADDRESS,
      paymentType: 'ETH',
      gasPrice: this.props.recommendedGasPrice,
      gasForward: null,
    };
  }

  /**
   * Get the proper amount to send, converted to tokenWei.
   *
   * @param {Object} formInput
   *
   * @returns {BN}
   */
  getAmountToSend(formInput = {}) {
    const { tokenList } = this.props;
    const input = {
      ...this.state,
      ...formInput,
    };
    const { tokenAddress } = input;
    const sendingTokens = tokenAddress !== ZERO_ADDRESS;
    const decimals = getPaymentDecimals(tokenAddress, tokenList);
    const amount = isValidFloat(input.amount, decimals) ? input.amount.toString() : '0';

    return sendingTokens
      ? convertTokenToLowestUnit(tokenList, amount, tokenAddress)
      : safeToBn(toWei(amount));
  }

  handleChange({
    amount, tokenAddress, gasPrice, paymentType, gasForward,
  }) {
    const isChangingToken = tokenAddress !== this.state.tokenAddress;
    this.setState({
      amount,
      tokenAddress,
      gasPrice,
      paymentType,
      gasForward,
    });

    this.canPayFee({
      amount,
      tokenAddress,
      paymentType,
    });

    // Update token address in store state - kick off balance fetch.
    if (isChangingToken) {
      this.props.onTokenAddressChange(tokenAddress);
    }
  }

  async calculateGasForward(input) {
    const { onReceiverBalanceFetch, acceptPaymentGasLimit, recommendedGasPrice } = this.props;
    if (!isAddress(input.destination)) {
      return safeToBn(0);
    }
    const balance = await onReceiverBalanceFetch(input.destination);
    let gasPrice = recommendedGasPrice;
    // Use backup if the recommended gas price didn't fetch properly.
    if (!gasPrice) {
      gasPrice = BACKUP_GAS_PRICE;
    }
    const gasPriceWei = toWei(gasPrice.toString(), 'gwei');
    const gasForward = safeToBn(gasPriceWei).mul(safeToBn(acceptPaymentGasLimit));
    // Forward gas if we know the balance, and it's not enough to cover accept/cancel txn.
    return balance && balance.lt(gasForward) ? gasForward : safeToBn(0);
  }

  async authorizeToken(amount, formattedAmount, tokenAddress) {
    const { authorizeToken, fetchTokenAllowance } = this.props;
    const tokenAllowance = await fetchTokenAllowance(tokenAddress);
    // Not sending tokens, or already authorized an allowance.
    if (tokenAddress === ZERO_ADDRESS || tokenAllowance.gte(amount)) {
      return true;
    }

    const transactionHash = await authorizeToken(
      amount,
      tokenAddress,
      toWei(this.state.gasPrice.toString(), 'gwei'),
    );
    // No hash means early failure or user cancelled.
    return !!transactionHash;
  }

  canPayFee(input, balanceOverride = null, catBalanceOverride = null) {
    const { payWithEthFee, payWithCatFee } = this.props;
    const balance = balanceOverride || this.props.balance;
    const catBalance = catBalanceOverride || this.props.catBalance;
    const { tokenAddress, paymentType } = input;
    const amountToSend = input.amount ? this.getAmountToSend(input) : safeToBn(0);

    // No balances or fees yet.
    if (!balance || !catBalance || !payWithEthFee || !payWithCatFee) {
      return false;
    }

    // Check if we have enough to pay for fees.
    let canPayFee = false;
    if (paymentType === 'CAT' && catBalance.gte(payWithCatFee)) {
      canPayFee = true;
    } else if (paymentType === 'ETH') {
      const totalEthAmount = tokenAddress === ZERO_ADDRESS ? amountToSend : safeToBn(0);
      if (balance.gt(totalEthAmount.add(payWithEthFee))) {
        canPayFee = true;
      }
    }
    if (this.mounted) {
      this.setState({ canPayFee });
    }
    return canPayFee;
  }

  async authorizeCatPayment() {
    const { payWithCatFee, catAllowance, authorizeCat } = this.props;
    if (safeToBn(payWithCatFee).gt(safeToBn(catAllowance))) {
      const transactionHash = await authorizeCat(
        payWithCatFee,
        toWei(this.state.gasPrice.toString(), 'gwei'),
      );
      // No hash means early failure or user cancelled.
      if (!transactionHash) {
        return false;
      }
    }
    return true;
  }

  async handleSubmit(input) {
    const { onSubmit, payWithEthFee, payWithCatFee } = this.props;
    const { destination, tokenAddress, paymentType } = input;
    const sendingTokens = tokenAddress !== ZERO_ADDRESS;
    const payWithCat = paymentType === 'CAT';
    const gasLimit = this.props.getCreatePaymentGasLimit(tokenAddress, payWithCat);

    const gasForward = await this.calculateGasForward(input);
    const amountToSend = this.getAmountToSend(input);
    const formattedAmount = this.props.formatPaymentAmount(amountToSend, tokenAddress, 18);
    const formattedPaymentFee = payWithCat ? (
      <FormattedWei wei={payWithCatFee} suffix="CAT" decimals={18} />
    ) : (
      <FormattedWei wei={payWithEthFee} decimals={18} />
    );

    const authorized = sendingTokens
      ? await this.authorizeToken(amountToSend, formattedAmount, tokenAddress)
      : true;

    if (authorized && this.canPayFee(input)) {
      const viewOptions = {
        title: 'Confirm Payment',
        message: 'This will send a payment to your selected address.',
        extraFields: omitBy(
          {
            'Sending To': {
              rowClass: 'address-row',
              component: (
                <React.Fragment>
                  <div className="address">
                    {destination}
                    <AddressImage address={destination} />
                  </div>
                  {!checkAddressChecksum(destination) && (
                    <div className="info">{ADDRESS_CHECKSUM_WARNING}</div>
                  )}
                </React.Fragment>
              ),
            },
            Amount: {
              component: <span>{!sendingTokens ? formattedAmount : '0 ETH'}</span>,
            },
            Tokens: {
              component: sendingTokens ? formattedAmount : null,
            },
            Fee: {
              component: formattedPaymentFee,
            },
          },
          field => !field.component,
        ),
        disclaimers: [
          'The recipient&apos;s wallet must be a supported wallet',
          'The recipient must visit pay.tabby.io to accept your payment',
          'Note: you will pay a small gas fee for this transaction.',
        ],
      };

      const canPay = payWithCat ? await this.authorizeCatPayment() : true;
      if (canPay) {
        const submitted = await onSubmit(
          {
            ...input,
            // Convert from BN.
            amount: amountToSend.toString(10),
            gasPrice: toWei(input.gasPrice.toString(), 'gwei'),
            gasForward,
            payWithCat,
            ethPaymentFee: payWithEthFee,
            catPaymentFee: payWithCatFee,
            source: 0,
            gasLimit,
            formattedTokenAmount: sendingTokens ? formattedAmount : null,
          },
          viewOptions,
        );
        // Reset internal state on success.
        if (submitted) {
          this.setState(this.getDefaultState());
          return true;
        }
      }
    }
    return false;
  }

  handleCustomTokenFormSubmit(tokenData) {
    this.props.addCustomToken(tokenData);
    return tokenData.address;
  }

  generateTokenOptions() {
    return [
      {
        // The contract recognizes sending ETH with token address of 0x0.
        value: ZERO_ADDRESS,
        label: 'ETH',
      },
    ].concat(this.props.tokenList.map((token) => {
      const { name, symbol, address } = token;
      return {
        value: address,
        label: `${name} (${symbol})`,
      };
    }));
  }

  render() {
    const {
      tokenAddress, paymentType, gasPrice, canPayFee, gasForward,
    } = this.state;
    const {
      currentAddress,
      recommendedGasPrice,
      payWithEthFee,
      payWithCatFee,
      tokenList,
      disabled,
      onWalletReset,
      validateTokenAddress,
      fetchTokenBalance,
      catEthExchangeRate,
      ethFiatExchangeRate,
      currencyCode,
    } = this.props;

    const amountToSend = this.getAmountToSend();
    const gasLimit = this.props.getCreatePaymentGasLimit(tokenAddress, paymentType === 'CAT');
    const gasFee = safeToBn(gasLimit).mul(safeToBn(toWei(gasPrice.toString(), 'gwei')));

    const payWithEthFeeFiat = weiToFiat(
      payWithEthFee,
      currencyCode,
      ethFiatExchangeRate,
      currencyCode.toUpperCase(),
    );
    const payWithCatFeeFiat = weiToFiat(
      payWithCatFee,
      currencyCode,
      BigNumber(catEthExchangeRate).times(ethFiatExchangeRate),
      currencyCode.toUpperCase(),
    );

    const disclaimer = (
      <div className="external-disclaimer">
        You cannot send payments with an external wallet. Please{' '}
        <button onClick={onWalletReset}>select a different wallet</button> if you wish to send a
        payment.
      </div>
    );

    return (
      <React.Fragment>
        <h2 className="section-header">New Payment</h2>
        <PaymentForm
          onChange={this.handleChange}
          currentAddress={currentAddress}
          recommendedGasPrice={recommendedGasPrice}
          tokenList={tokenList}
          tokenOptions={this.generateTokenOptions()}
          onSubmit={this.handleSubmit}
          disabled={disabled}
          onSelectFormSubmit={this.handleCustomTokenFormSubmit}
          getAmountToSend={this.getAmountToSend}
          validateTokenAddress={validateTokenAddress}
          calculateGasForward={this.calculateGasForward}
          fetchTokenBalance={fetchTokenBalance}
          payWithEthFee={payWithEthFee}
          payWithEthFeeFiat={payWithEthFeeFiat}
          payWithCatFee={payWithCatFee}
          payWithCatFeeFiat={payWithCatFeeFiat}
        >
          <CostCalculatorContainer
            amount={amountToSend}
            tokenAddress={tokenAddress}
            paymentType={paymentType}
            payWithEthFee={payWithEthFee}
            payWithCatFee={payWithCatFee}
            canPayFee={canPayFee}
            gasFee={gasFee}
            gasForward={gasForward}
          />
        </PaymentForm>
        {disabled && disclaimer}
      </React.Fragment>
    );
  }
}

PaymentFormContainer.propTypes = PaymentForm.propTypes;

PaymentFormContainer.displayName = 'PaymentFormContainer';

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(PaymentFormContainer);



// WEBPACK FOOTER //
// ./src/components/PaymentFormContainer.js